import React from 'react'
import { observer, useLocalObservable } from "mobx-react-lite"
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import classes from './Profile.module.scss'
import OutlinedInput from '@mui/material/OutlinedInput';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import userStore from '../common/userStore';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import { runInAction, toJS } from 'mobx';
import clsx from 'clsx';
import axios from 'axios'
import {addSuccess, addError} from '../../components/Notifier'
import LoadingButton from '@mui/lab/LoadingButton';
import Password from './Password'
import CheckIcon from '@mui/icons-material/Check';
import { requestResetPassword, updateUser, requestVerifyEmail, requestChangeEmail } from '../account/AccountAPI'
import useTitle from '../../hooks/title';


const Field = observer(({label, children, labelAction, className, error}) => {
  return <div className={clsx(classes.field, className)}>
  <div className={classes.arrow}><PlayArrowIcon /></div>
  <div className={classes.column}>
    <div className={classes.label}>{label}{labelAction}</div>
    <div className={classes.edit}>
      {children}
    </div>
    {error ? <div className={classes.error}>
      {error}
    </div> : null}
  </div>
</div>
})

const ChangeEmailForm = observer(({form}) => {
  const changeForm = useLocalObservable(() => ({
    email: '',
    password: '',
    loading: false,
    errors: {email:null, password:null},
    immediately: false,
    requiredPassword: !!form.user.isVerified,
    handleEmail(e) {
      this.email = e.target.value
      this.immediately && this.validEmail()
    },
    validEmail() {
      if (this.email.length < 1) {
        this.errors.email = 'Email is required'
        return false
      }
      const pattern = /[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w](?:[\w-]*[\w])?\.)+[\w](?:[\w-]*[\w])?/g
      if (!(pattern.test(this.email.toString()))) {
        this.errors.email = 'Email is invalid'
        return false
      }
      this.errors.email = null
      return true
    },
    handlePassword(e) {
      this.password = e.target.value
      this.immediately && this.validPassword()
    },
    validPassword() {
      if (!this.requiredPassword) {
        return true
      }
      if (this.password.length < 1) {
        this.errors.password = 'Password is required'
        return false
      }
      this.errors.password = null
      return true
    },
    async handleSubmit() {
      try {
        this.immediately = true
        const rs = [this.validEmail(), this.validPassword()]
        if (rs.some(r => !r)) {
          return;
        }
        this.loading = true
        if (this.requiredPassword) {
          await requestChangeEmail({newEmail: this.email, password: this.password})
        } else {
          await requestChangeEmail({newEmail: this.email})
        }
        addSuccess(`We’ve sent an email confirmation link to ${this.email}`)
        form.closeChangeEmail()
      } catch (e) {
        console.error('change fail', e)
        if (this.requiredPassword) {
          runInAction(() => this.errors.password = e.message.toString().includes('password') ? 'Incorrect password' : 'Email already exists')
        }
      } finally {
        runInAction(() => this.loading = false)
      }
    },
    handleKeyup(e) {
      if(e.keyCode === 13) {
        this.handleSubmit()
      }
    }
  }))

  return <div className={classes.changeDailog}>
    <div className={classes.changeHead}><CloseIcon onClick={form.closeChangeEmail} /></div>
    <h2>Change your email</h2>
    <OutlinedInput 
          className={classes.newEmail}
          value={changeForm.email}
          onChange={changeForm.handleEmail}
          autoComplete="off"
          name="newEmail"
          size="small" 
          placeholder="New email address" />
    {changeForm.errors.email ? <div className={classes.changeError}>{changeForm.errors.email}</div> : null}
    {changeForm.requiredPassword ? <Password
      className={classes.changePwd}
      value={changeForm.password}
      onChange={changeForm.handlePassword}
      autoComplete="off"
      name="currentPassword"
      size="small" 
      placeholder="Current password" /> : null}
    {changeForm.errors.password ? <div className={classes.changeError}>{changeForm.errors.password}</div> : null} 
    {changeForm.requiredPassword ? <div className={classes.changeForgot} ><Link underline="hover" onClick={form.handleResetPassword}>Forgot password?</Link></div> : null}

    <div className={classes.space}></div>
    <LoadingButton className={classes.changeSubmit}
      variant="contained" 
      disableElevation 
      onClick={changeForm.handleSubmit}
      loading={changeForm.loading}>Send change email link</LoadingButton>
  </div>
})

const ChangeEmailDailog = observer(({form}) => {
  return <Modal open={form.changeEmail} onClose={form.closeChangeEmail}>
    <ChangeEmailForm form={form} />
  </Modal>
})

const Profile = observer(() => {
  useTitle('Account Profile')

  const form = useLocalObservable(() => ({
    user: toJS(userStore.user),
    errors: {},
    sending: false,
    changeUsername: false,
    changeEmail: false,
    postingUsername: false,
    changed: false,
    postingInfo: false,
    requiredValidEmail: false,
    handleSocialName(e) {
      this.user.socialName = e.target.value
      this.errors.socialName = null
    },
    async updateSocialName() {
      if (this.postingUsername) {
        return;
      }
      try {
        this.postingUsername = true
        if (this.user.socialName.length < 1) {
          this.errors.socialName = 'Username is required'
          return;
        }
        const pattern = /^[_\.a-zA-Z0-9]+$/g;
        if (!pattern.test(this.user.socialName)) {
          this.errors.socialName = 'Username can only use letters, numbers, underscores and periods.'
          return;
        }
        await updateUser({socialName: this.user.socialName})
        await this.reload()
        this.closeChangeUsername()
      } catch (e) {
        this.errors.socialName = 'Username already used'
      } finally {
        runInAction(() => this.postingUsername = false)
      }
    },
    handleName(e) {
      this.user.name = e.target.value
      this.changed = true;
    },
    handleBio(e) {
      this.user.bio = e.target.value
      this.changed = true;
    },
    async updateInfo() {
      if (this.postingInfo) {
        return;
      }
      try {
        this.postingInfo = true
        await updateUser({name: this.user.name, bio: this.user.bio})
        await this.reload()
        runInAction(() => this.changed = false)
      } catch (e) {
        console.error('update info fail', e)
      } finally {
        runInAction(() => this.postingInfo = false)
      }
    },
    async reload() {
      await userStore.load()
      this.user = toJS(userStore.user)
    },
    reset() {
      this.user = toJS(userStore.user)
    },
    uploadAvatar(e) {
      e.preventDefault();
      const file = e.target.files[0];
      console.log(file)
      const formData = new FormData();
      formData.append("file", file);
      const config = {
        headers: { "Content-Type": "multipart/form-data" }
      };
      axios.post("/user/uploadAvatar", formData, config).then(res => {
        this.reload()
      }).catch(() => {
        addError('Change fail')
      });
    },
    async handleResetPassword() {
      if (this.sending) {
        return;
      }
      this.sending = true
      try {
        const email = this.user.email
        await requestResetPassword({email})
        addSuccess(`We’ve sent a password reset request to ${email}`)
      } catch(e) {
        console.log('send fail', e)
        addError(`Send fail`)
      } finally {
        this.sending = false
      }
    },
    async handleVerifyEmail() {
      if (this.sending) {
        return;
      }
      this.sending = true
      try {
        const email = this.user.email
        await requestVerifyEmail({})
        addSuccess(`We’ve sent an email confirimation link to ${email}`)
      } catch (e) {
        console.log('send fail', e)
        addError(`Send fail`)
      } finally {
        this.sending = false
      }
    },
    openChangeEmail() {
      this.changeEmail = true
    },
    closeChangeEmail() {
      this.changeEmail = false
    },
    openChangeUsername() {
      if (this.user.isVerified == 0) {
        this.requiredValidEmail = true
        return;
      }
      this.changeUsername = true
    },
    closeChangeUsername() {
      this.changeUsername = false
      this.user.socialName = userStore.user.socialName || ''
      this.errors.socialName = null
    }
  }))

  return <div className={classes.root}>
    <div className={classes.left}>
      <div className={classes.avatar}>
        <Avatar className={classes.img} src={form.user.avatar}></Avatar>
        <IconButton className={classes.upload}><ModeEditIcon /></IconButton>
        <div className={classes.hoverWrap}></div>
        <div className={classes.hover} >
          <AddAPhotoIcon />
          <div className={classes.hoverTips}>Change Photo</div>
        </div>
        <input type="file" name="file" className={classes.file} multiple={false}
          accept="image/png,image/jpg,image/jpeg,image/gif" onChange={form.uploadAvatar} />
      </div>
    </div>
    <div className={classes.right}>
      {/* username */}
      {form.changeUsername ? <Field label="Username" className={classes.username} 
          error={form.errors.socialName ? <div className={classes.usernameTips}><ErrorOutlineIcon />{form.errors.socialName}</div> : <div className={classes.usernameTips}><ErrorOutlineIcon />You can change your username only once</div>}>
        <div className={classes.text} style={{flex: 0}}>
          {/* sessionloops.com/ */}
        </div>
        <OutlinedInput autoFocus name="socialName" 
            className={classes.usernameInput} 
            value={form.user.socialName}
            onChange={form.handleSocialName}  />
        <CloseIcon className={classes.cancelChange} onClick={form.closeChangeUsername} />
        <CheckIcon className={classes.applyChange} onClick={form.updateSocialName} />
      </Field> : <Field label="Username" className={classes.username + " " + classes.readonly} error={form.requiredValidEmail && <div className={classes.usernameTips}><ErrorOutlineIcon />You must have a verified email first.</div>}>
        {form.user.socialName ? <div className={classes.text}>
          {/* sessionloops.com/ */}
          <span className={classes.textEdit}>{form.user.socialName}</span></div>: null}
        {!form.user.socialName ? <Link underline="none" className={classes.action} onClick={form.openChangeUsername}>Set Username</Link> : null}
      </Field>}
      {/* email */}
      <Field label="Email">
        <div className={classes.text}>{form.user.email}</div>
        {form.user.isVerified ? null : <Link underline="none" className={classes.action} onClick={form.handleVerifyEmail}>Verify Email</Link>}
        <Link underline="none" className={classes.action} onClick={form.openChangeEmail}>Change Email</Link>
      </Field>
      {/* Profile Name */}
      <Field label="Profile Name">
        <OutlinedInput 
          className={classes.input}
          value={form.user.name || ''}
          onChange={form.handleName}
          autoComplete="off"
          name="profileName"
          size="small" 
          placeholder="" />
      </Field>
      {/* Bio */}
      <Field label="Bio">
        <OutlinedInput 
          multiline
          className={classes.textarea}
          value={form.user.bio || ''}
          onChange={form.handleBio}
          inputComponent="textarea"
          inputProps={{rows: 7}}
          autoComplete="off"
          name="bio"
          size="small" 
          placeholder="" />
      </Field>
      {/* password */}
      <Field label="Password" labelAction={<Link underline="none" className={classes.action} 
          onClick={form.handleResetPassword}>Change Password</Link>}>
      </Field>

      <div className={classes.actions}>
        <Button variant="outlined" 
          onClick={form.reset}
          disabled={!form.changed}>Cancel</Button>
        <LoadingButton variant="contained" 
          disableElevation 
          loading={form.postingInfo} 
          disabled={!form.changed} 
          onClick={form.updateInfo}>Save</LoadingButton>
      </div>
    </div>
    <ChangeEmailDailog form={form} />
  </div>
})

export default Profile;